import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { fetchData, fetchJson } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import {
  Button,
  Card,
  Currency,
  Empty,
  ErrorBanner,
  Field,
  FormattedDate,
  Input,
  Loading,
  PageHeader,
  Select,
  Textarea,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'

type ClientPayment = {
  id: string
  project_id: string
  payment_date?: number
  amount?: number
  payment_mode?: string | null
  reference_number?: string | null
  description?: string | null
}

export function ClientPaymentsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()

  const list = useQuery<ClientPayment[]>({
    queryKey: ['client-payments', projectId],
    enabled: Boolean(projectId),
    queryFn: () => fetchData<ClientPayment[]>(orgPath(`/projects/${projectId}/payments`)),
  })

  const [amount, setAmount] = useState('')
  const [mode, setMode] = useState('bank_transfer')
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')

  const create = useMutation({
    mutationFn: () =>
      fetchJson(orgPath('/client-payments'), {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          amount: Number(amount),
          payment_mode: mode,
          reference_number: reference || undefined,
          description: notes || undefined,
          payment_date: Math.floor(Date.now() / 1000),
        }),
      }),
    onError: flagIfAuthError,
    onSuccess: () => {
      setAmount('')
      setReference('')
      setNotes('')
      qc.invalidateQueries({ queryKey: ['client-payments', projectId] })
    },
  })

  if (list.isLoading) return <Loading />
  if (list.isError) return <ErrorBanner error={list.error} />

  return (
    <div className="space-y-6">
      <PageHeader title="Client payments" subtitle="Inflows credited to this project." />

      <Card>
        <h2 className="font-semibold mb-3">Record payment</h2>
        <form
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          <Field label="Amount">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </Field>
          <Field label="Mode">
            <Select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="bank_transfer">Bank transfer</option>
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
            </Select>
          </Field>
          <Field label="Reference (optional)">
            <Input value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Notes (optional)">
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
          {create.isError && (
            <div className="sm:col-span-2">
              <ErrorBanner error={create.error} />
            </div>
          )}
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Saving…' : 'Record payment'}
            </Button>
          </div>
        </form>
      </Card>

      <section>
        <h2 className="font-semibold mb-3">History</h2>
        {!list.data || list.data.length === 0 ? (
          <Empty message="No client payments yet." />
        ) : (
          <Card>
            <ul className="divide-y divide-surface-700 text-sm">
              {list.data.map((p) => (
                <li key={p.id} className="py-2 flex justify-between gap-3">
                  <div>
                    <Currency amount={p.amount ?? null} />
                    {p.payment_mode && (
                      <span className="text-xs text-surface-400 ml-2">{p.payment_mode}</span>
                    )}
                    {p.description && (
                      <div className="text-xs text-surface-400 mt-0.5">{p.description}</div>
                    )}
                  </div>
                  <div className="text-xs text-surface-400 text-right">
                    <FormattedDate ts={p.payment_date ?? null} />
                    {p.reference_number && <div>{p.reference_number}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>
    </div>
  )
}
